import React, { useState, useEffect } from 'react';
import {
  Box,
  Grid,
  Card,
  CardContent,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  Chip,
  IconButton,
  Alert,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
  Paper,
  Stack
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import useMediaQuery from '@mui/material/useMediaQuery';
import {
  TrendingUp as TrendingUpIcon,
  People as PeopleIcon,
  Assessment as AssessmentIcon,
  Timeline as TimelineIcon,
  Speed as SpeedIcon,
  Assignment as TaskIcon,
  Phone as PhoneIcon,
  Email as EmailIcon,
  Event as MeetingIcon,
  Visibility as DemoIcon,
  LocationOn as SiteVisitIcon,
  Refresh as RefreshIcon
} from '@mui/icons-material';
import { useOrg } from '../../contexts/OrgContext';
import Breadcrumbs from '../../components/Breadcrumbs';
import KpiCard from '../../components/crm/KpiCard';
import StatusBadge from '../../components/crm/StatusBadge';
import PipelineChart from '../../components/crm/PipelineChart';
import { 
  getCrmKpis, 
  getPipeline, 
  getReps, 
  getActivities, 
  getAtRisk,
  getTerritories 
} from '../../services/mock/crmService';
import { ActivityTypes } from '../../types/crm';

const CrmDashboard = () => {
  const { org } = useOrg();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [kpis, setKpis] = useState({});
  const [pipeline, setPipeline] = useState([]);
  const [reps, setReps] = useState([]);
  const [activities, setActivities] = useState([]);
  const [atRisk, setAtRisk] = useState([]);
  const [territories, setTerritories] = useState([]);
  const [loading, setLoading] = useState(true);
  
  // Filters
  const [dateRange, setDateRange] = useState('30d');
  const [selectedRep, setSelectedRep] = useState('all');
  const [activityFilter, setActivityFilter] = useState('all');

  // Feature flags
  const showGMV = process.env.REACT_APP_SHOW_GMV === 'true';
  const enableTerritory = process.env.REACT_APP_ENABLE_TERRITORY === 'true';

  useEffect(() => {
    loadData();
  }, [org, dateRange, selectedRep]);

  const loadData = async () => {
    setLoading(true);
    try {
      const filters = { dateRange, repId: selectedRep === 'all' ? null : selectedRep };
      
      const [kpisData, pipelineData, repsData, activitiesData, atRiskData, territoriesData] = 
        await Promise.all([
          getCrmKpis(org, filters),
          getPipeline(org, filters),
          getReps(org),
          getActivities(org, filters, 15),
          getAtRisk(org, filters),
          enableTerritory ? getTerritories(org) : Promise.resolve([])
        ]);

      setKpis(kpisData);
      setPipeline(pipelineData);
      setReps(repsData);
      setActivities(activitiesData);
      setAtRisk(atRiskData);
      setTerritories(territoriesData);
    } catch (error) {
      console.error('Error loading CRM data:', error);
    } finally {
      setLoading(false);
    }
  };

  const getActivityIcon = (type) => {
    switch (type) {
      case ActivityTypes.CALL: return <PhoneIcon sx={{ fontSize: 16 }} />;
      case ActivityTypes.EMAIL: return <EmailIcon sx={{ fontSize: 16 }} />;
      case ActivityTypes.MEETING: return <MeetingIcon sx={{ fontSize: 16 }} />;
      case ActivityTypes.DEMO: return <DemoIcon sx={{ fontSize: 16 }} />;
      case ActivityTypes.SITE_VISIT: return <SiteVisitIcon sx={{ fontSize: 16 }} />;
      default: return <TaskIcon sx={{ fontSize: 16 }} />;
    }
  };

  const formatCurrency = (value) => `$${value?.toLocaleString() || 0}`;
  const formatPercent = (value) => `${((value || 0) * 100).toFixed(1)}%`;

  const filteredActivities = activities.filter(activity => 
    activityFilter === 'all' || activity.type === activityFilter
  );

  return (
    <Box>
      <Breadcrumbs />
      
      {/* Header */}
      <Box sx={{ mb: 4, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <Box>
          <Typography variant="h3" component="h1" sx={{ fontWeight: 700, color: '#0f172a', mb: 1 }}>
            CRM Dashboard
          </Typography>
          <Typography variant="h6" sx={{ color: '#64748b', fontWeight: 500 }}>
            {org === 'bare' ? 'Bare Biologics - Sales Operations' : 'EPL - Sales Operations'}
          </Typography>
        </Box>
        
        {/* Filters */}
        <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
          <FormControl size="small" sx={{ minWidth: 120 }}>
            <InputLabel>Date Range</InputLabel>
            <Select
              value={dateRange}
              onChange={(e) => setDateRange(e.target.value)}
              label="Date Range"
            >
              <MenuItem value="7d">Last 7 days</MenuItem>
              <MenuItem value="30d">Last 30 days</MenuItem>
              <MenuItem value="90d">Last 90 days</MenuItem>
              <MenuItem value="ytd">Year to Date</MenuItem>
            </Select>
          </FormControl>
          
          <FormControl size="small" sx={{ minWidth: 140 }}>
            <InputLabel>Sales Rep</InputLabel>
            <Select
              value={selectedRep}
              onChange={(e) => setSelectedRep(e.target.value)}
              label="Sales Rep"
            >
              <MenuItem value="all">All Reps</MenuItem>
              {reps.map(rep => (
                <MenuItem key={rep.id} value={rep.id}>{rep.name}</MenuItem>
              ))}
            </Select>
          </FormControl>
          
          <IconButton onClick={loadData} disabled={loading}>
            <RefreshIcon />
          </IconButton>
        </Box>
      </Box>

      {/* KPI Cards */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        {showGMV && (
          <Grid item xs={12} sm={6} md={2}>
            <KpiCard
              title="Total Revenue"
              value={kpis.totalRevenue}
              subtitle="last 90 days"
              icon={<TrendingUpIcon />}
              color={org === 'bare' ? '#5c725c' : '#0d9488'}
              formatter={formatCurrency}
              trend="up"
              trendValue={12}
            />
          </Grid>
        )}
        
        <Grid item xs={12} sm={6} md={showGMV ? 2 : 2}>
          <KpiCard
            title="Open Pipeline"
            value={kpis.openPipelineUnits}
            subtitle="estimated monthly units"
            icon={<AssessmentIcon />}
            color="#f59e0b"
          />
        </Grid>
        
        <Grid item xs={12} sm={6} md={showGMV ? 2 : 2}>
          <KpiCard
            title="Win Rate"
            value={kpis.winRate90d}
            subtitle="last 90 days"
            icon={<TimelineIcon />}
            color="#10b981"
            formatter={formatPercent}
          />
        </Grid>
        
        <Grid item xs={12} sm={6} md={showGMV ? 2 : 2}>
          <KpiCard
            title="Sales Velocity"
            value={kpis.velocityDays}
            subtitle="avg days to close"
            icon={<SpeedIcon />}
            color="#8b5cf6"
            formatter={(val) => `${val} days`}
          />
        </Grid>
        
        <Grid item xs={12} sm={6} md={showGMV ? 2 : 2}>
          <KpiCard
            title="Active Reps"
            value={kpis.activeReps}
            subtitle="current team size"
            icon={<PeopleIcon />}
            color="#039582"
          />
        </Grid>
        
        <Grid item xs={12} sm={6} md={showGMV ? 2 : 2}>
          <KpiCard
            title="Activities"
            value={kpis.activities7d}
            subtitle={`${kpis.activities30d} last 30d`}
            icon={<TaskIcon />}
            color="#ef4444"
            formatter={(val) => `${val} (7d)`}
          />
        </Grid>
      </Grid>

      {/* Second Row */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        {/* Pipeline Chart */}
        <Grid item xs={12} md={4}>
          <Card sx={{ border: '1px solid #e2e8f0', height: '400px' }}>
            <CardContent sx={{ p: 3, height: '100%' }}>
              <PipelineChart deals={pipeline} />
            </CardContent>
          </Card>
        </Grid>
        
        {/* Rep Leaderboard */}
        <Grid item xs={12} md={8}>
          <Card sx={{ border: '1px solid #e2e8f0', height: '400px' }}>
            <CardContent sx={{ p: 0, height: '100%' }}>
              <Box sx={{ p: 3, bgcolor: '#f8fafc', borderBottom: '1px solid #e2e8f0' }}>
                <Typography variant="h6" sx={{ fontWeight: 600, color: '#0f172a' }}>
                  Rep Leaderboard
                </Typography>
                <Typography variant="body2" sx={{ color: '#64748b' }}>
                  Performance last 90 days
                </Typography>
              </Box>
              <TableContainer sx={{ maxHeight: '300px' }}>
                <Table stickyHeader size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell sx={{ fontWeight: 600, bgcolor: '#f8fafc' }}>Rep</TableCell>
                      <TableCell sx={{ fontWeight: 600, bgcolor: '#f8fafc' }}>Deals Won</TableCell>
                      <TableCell sx={{ fontWeight: 600, bgcolor: '#f8fafc' }}>Units Won</TableCell>
                      <TableCell sx={{ fontWeight: 600, bgcolor: '#f8fafc' }}>Win Rate</TableCell>
                      <TableCell sx={{ fontWeight: 600, bgcolor: '#f8fafc' }}>Activities</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {reps.filter(r => r.active).map(rep => {
                      const wonDeals = Math.floor(2 + Math.random() * 8);
                      const unitsWon = Math.floor(50 + Math.random() * 200);
                      const winRate = Math.random() * 0.4 + 0.3;
                      const activitiesCount = Math.floor(15 + Math.random() * 25);
                      
                      return (
                        <TableRow key={rep.id} hover>
                          <TableCell>
                            <Box>
                              <Typography variant="body2" sx={{ fontWeight: 600 }}>
                                {rep.name}
                              </Typography>
                              <Typography variant="caption" sx={{ color: '#64748b' }}>
                                {rep.territory}
                              </Typography>
                            </Box>
                          </TableCell>
                          <TableCell>{wonDeals}</TableCell>
                          <TableCell>{unitsWon.toLocaleString()}</TableCell>
                          <TableCell>{formatPercent(winRate)}</TableCell>
                          <TableCell>{activitiesCount}</TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </TableContainer>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Third Row */}
      <Grid container spacing={3}>
        {/* Activity Feed */}
        <Grid item xs={12} md={enableTerritory ? 8 : 12}>
          <Card sx={{ border: '1px solid #e2e8f0' }}>
            <CardContent sx={{ p: 0 }}>
              <Box sx={{ p: 3, bgcolor: '#f8fafc', borderBottom: '1px solid #e2e8f0' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
                  <Typography variant="h6" sx={{ fontWeight: 600, color: '#0f172a' }}>
                    Activity Feed
                  </Typography>
                  <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                    <Chip 
                      label="All" 
                      variant={activityFilter === 'all' ? 'filled' : 'outlined'}
                      size="small"
                      onClick={() => setActivityFilter('all')}
                      sx={{ cursor: 'pointer' }}
                    />
                    {Object.values(ActivityTypes).map(type => (
                      <Chip 
                        key={type}
                        label={type}
                        variant={activityFilter === type ? 'filled' : 'outlined'}
                        size="small"
                        onClick={() => setActivityFilter(type)}
                        sx={{ cursor: 'pointer' }}
                      />
                    ))}
                  </Box>
                </Box>
              </Box>
              
              <Box sx={{ p: 3, maxHeight: '400px', overflowY: 'auto' }}>
                {filteredActivities.map(activity => (
                  <Box 
                    key={activity.id} 
                    sx={{ 
                      display: 'flex', 
                      gap: 2, 
                      mb: 2, 
                      pb: 2, 
                      borderBottom: '1px solid #f1f5f9',
                      '&:last-child': { borderBottom: 'none' }
                    }}
                  >
                    <Box sx={{ 
                      mt: 0.5,
                      color: '#64748b'
                    }}>
                      {getActivityIcon(activity.type)}
                    </Box>
                    <Box sx={{ flex: 1 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 0.5 }}>
                        <Typography variant="body2" sx={{ fontWeight: 600 }}>
                          {activity.type}
                        </Typography>
                        <StatusBadge status={activity.outcome} size="small" />
                        <Typography variant="caption" sx={{ color: '#64748b' }}>
                          {new Date(activity.ts).toLocaleDateString()}
                        </Typography>
                      </Box>
                      <Typography variant="body2" sx={{ color: '#374151', mb: 0.5 }}>
                        {activity.companyName}
                      </Typography>
                      <Typography variant="body2" sx={{ color: '#64748b', fontSize: '0.8rem' }}>
                        {activity.note}
                      </Typography>
                    </Box>
                  </Box>
                ))}
              </Box>
            </CardContent>
          </Card>
        </Grid>

        {/* Territory Snapshot */}
        {enableTerritory && (
          <Grid item xs={12} md={4}>
            <Card sx={{ border: '1px solid #e2e8f0' }}>
              <CardContent sx={{ p: 3 }}>
                <Typography variant="h6" sx={{ fontWeight: 600, color: '#0f172a', mb: 3 }}>
                  Territory Snapshot
                </Typography>
                {territories.map(territory => (
                  <Box key={territory.name} sx={{ mb: 2 }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                      <Typography variant="body2" sx={{ fontWeight: 600 }}>
                        {territory.name}
                      </Typography>
                      <Typography variant="body2" sx={{ color: '#64748b' }}>
                        {territory.reps} reps
                      </Typography>
                    </Box>
                    <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
                      <Typography variant="caption" sx={{ minWidth: '60px', color: '#64748b' }}>
                        Revenue:
                      </Typography>
                      <Box sx={{ flex: 1, height: 4, bgcolor: '#f1f5f9', borderRadius: 1 }}>
                        <Box
                          sx={{
                            height: '100%',
                            width: `${Math.min((territory.revenue / 250000) * 100, 100)}%`,
                            bgcolor: org === 'bare' ? '#5c725c' : '#0d9488',
                            borderRadius: 1
                          }}
                        />
                      </Box>
                      <Typography variant="caption" sx={{ color: '#374151' }}>
                        {formatCurrency(territory.revenue)}
                      </Typography>
                    </Box>
                  </Box>
                ))}
              </CardContent>
            </Card>
          </Grid>
        )}
      </Grid>

      {/* At-Risk Accounts */}
      <Grid container spacing={3} sx={{ mt: 2 }}>
        <Grid item xs={12}>
          <Card sx={{ border: '1px solid #e2e8f0' }}>
            <CardContent sx={{ p: 0 }}>
              <Box sx={{ p: 3, bgcolor: '#f8fafc', borderBottom: '1px solid #e2e8f0' }}>
                <Typography variant="h6" sx={{ fontWeight: 600, color: '#0f172a' }}>
                  At-Risk Accounts
                </Typography>
                <Typography variant="body2" sx={{ color: '#64748b' }}>
                  Accounts requiring immediate attention
                </Typography>
              </Box>
              
              {isMobile ? (
                <Stack spacing={1.5} sx={{ p: 2 }}>
                  {atRisk.map((account) => (
                    <Paper key={account.companyId} variant="outlined" sx={{ p: 2 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
                        <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                          {account.companyName}
                        </Typography>
                        <StatusBadge status={account.risk} />
                      </Box>
                      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5, mb: 1.5 }}>
                        <Typography variant="body2" color="text.secondary">
                          Rep: {account.repName}
                        </Typography>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Typography variant="body2" color="text.secondary">Reason:</Typography>
                          <StatusBadge status={account.reason} />
                        </Box>
                        <Typography variant="body2" color="text.secondary">
                          Last Contact: {account.lastContact}
                        </Typography>
                      </Box>
                      <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                        <Button size="small" variant="outlined" sx={{ flex: 1 }}>
                          Create Task
                        </Button>
                        <Button size="small" variant="outlined" sx={{ flex: 1 }}>
                          Log Activity
                        </Button>
                      </Box>
                    </Paper>
                  ))}
                </Stack>
              ) : (
                <TableContainer sx={{ width: '100%', overflowX: 'auto' }}>
                  <Table sx={{ minWidth: 900 }}>
                    <TableHead>
                      <TableRow sx={{ bgcolor: '#fafbfc' }}>
                        <TableCell sx={{ fontWeight: 600 }}>Company</TableCell>
                        <TableCell sx={{ fontWeight: 600 }}>Rep</TableCell>
                        <TableCell sx={{ fontWeight: 600 }}>Reason</TableCell>
                        <TableCell sx={{ fontWeight: 600 }}>Last Contact</TableCell>
                        <TableCell sx={{ fontWeight: 600 }}>Risk Level</TableCell>
                        <TableCell sx={{ fontWeight: 600 }}>Actions</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {atRisk.map(account => (
                        <TableRow key={account.companyId} hover>
                          <TableCell sx={{ fontWeight: 600 }}>
                            {account.companyName}
                          </TableCell>
                          <TableCell>{account.repName}</TableCell>
                          <TableCell>
                            <StatusBadge status={account.reason} />
                          </TableCell>
                          <TableCell>{account.lastContact}</TableCell>
                          <TableCell>
                            <StatusBadge status={account.risk} />
                          </TableCell>
                          <TableCell>
                            <Box sx={{ display: 'flex', gap: 1 }}>
                              <Button size="small" variant="outlined">
                                Create Task
                              </Button>
                              <Button size="small" variant="outlined">
                                Log Activity  
                              </Button>
                            </Box>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </Box>
  );
};

export default CrmDashboard;
